<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { formatDistanceToNow, fromUnixTime } from 'date-fns';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  activeSort: {
    type: String,
    default: 'last_activity_at',
  },
  activeOrdering: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['toggleContact', 'toggleAll', 'update:sort']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const selectedIdsSet = computed(() => new Set(props.selectedContactIds));
const allContactsSelected = computed(
  () =>
    props.contacts.length > 0 &&
    props.contacts.every(contact => selectedIdsSet.value.has(contact.id))
);

const countriesMap = countries.reduce((map, country) => {
  map[country.id] = country;
  return map;
}, {});

const onClickViewDetails = id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  router.push({ name, params, query: route.query });
};

const toggleContact = (id, value) => {
  emit('toggleContact', { id, value });
};

const toggleAll = event => {
  emit('toggleAll', event.target.checked);
};

const updateSort = sort => {
  const order =
    props.activeSort === sort && props.activeOrdering === '' ? '-' : '';
  emit('update:sort', { sort, order });
};

const sortIcon = sort => {
  if (props.activeSort !== sort) return 'i-lucide-chevrons-up-down';
  return props.activeOrdering === '-'
    ? 'i-lucide-chevron-down'
    : 'i-lucide-chevron-up';
};

const getCompany = contact =>
  contact.additionalAttributes?.companyName ||
  t('CONTACTS_LAYOUT.TABLE.NO_COMPANY');

const getLocation = contact => {
  const attributes = contact.additionalAttributes || {};
  const countryKey = attributes.country || attributes.countryCode;
  const country = countriesMap[countryKey]?.name || countryKey;
  return (
    [attributes.city, country].filter(Boolean).join(', ') ||
    t('CONTACTS_LAYOUT.TABLE.NO_LOCATION')
  );
};

const getCountryCode = contact => {
  const attributes = contact.additionalAttributes || {};
  const countryKey = attributes.country || attributes.countryCode;
  return countriesMap[countryKey]?.id || '';
};

const getChannels = contact => {
  const names = (contact.contactInboxes || [])
    .map(contactInbox => contactInbox.inbox?.name)
    .filter(Boolean);
  return [...new Set(names)];
};

const formatLastActivity = timestamp => {
  if (!timestamp) return t('CONTACTS_LAYOUT.TABLE.NO_ACTIVITY');
  return formatDistanceToNow(fromUnixTime(timestamp), { addSuffix: true });
};
</script>

<template>
  <div class="overflow-x-auto rounded-lg border border-n-weak bg-n-surface-1">
    <table class="w-full min-w-[960px] table-fixed border-collapse">
      <thead class="bg-n-alpha-1">
        <tr class="border-b border-n-weak">
          <th class="w-12 px-4 py-3 text-start">
            <Checkbox
              :model-value="allContactsSelected"
              :aria-label="$t('CONTACTS_LAYOUT.TABLE.SELECT_ALL')"
              @change="toggleAll"
            />
          </th>
          <th class="w-[24%] px-3 py-3 text-start">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
              @click="updateSort('name')"
            >
              {{ $t('CONTACTS_LAYOUT.TABLE.CONTACT') }}
              <span class="size-3.5" :class="[sortIcon('name')]" />
            </button>
          </th>
          <th class="w-[22%] px-3 py-3 text-start">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
              @click="updateSort('email')"
            >
              {{ $t('CONTACTS_LAYOUT.TABLE.CONTACT_DETAILS') }}
              <span class="size-3.5" :class="[sortIcon('email')]" />
            </button>
          </th>
          <th class="w-[16%] px-3 py-3 text-start">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
              @click="updateSort('company_name')"
            >
              {{ $t('CONTACTS_LAYOUT.TABLE.COMPANY') }}
              <span class="size-3.5" :class="[sortIcon('company_name')]" />
            </button>
          </th>
          <th class="w-[14%] px-3 py-3 text-start">
            <span class="text-xs font-medium text-n-slate-10">
              {{ $t('CONTACTS_LAYOUT.TABLE.CHANNELS') }}
            </span>
          </th>
          <th class="w-[14%] px-3 py-3 text-start">
            <span class="text-xs font-medium text-n-slate-10">
              {{ $t('CONTACTS_LAYOUT.TABLE.LOCATION') }}
            </span>
          </th>
          <th class="w-[14%] px-3 py-3 text-start">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
              @click="updateSort('last_activity_at')"
            >
              {{ $t('CONTACTS_LAYOUT.TABLE.LAST_ACTIVITY') }}
              <span class="size-3.5" :class="[sortIcon('last_activity_at')]" />
            </button>
          </th>
          <th class="w-12 px-3 py-3">
            <span class="sr-only">
              {{ $t('CONTACTS_LAYOUT.TABLE.ACTIONS') }}
            </span>
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak">
        <tr
          v-for="contact in contacts"
          :key="contact.id"
          class="group h-16 transition-colors hover:bg-n-alpha-1"
          :class="{ 'bg-n-brand/5': selectedIdsSet.has(contact.id) }"
        >
          <td class="px-4 py-2">
            <Checkbox
              :model-value="selectedIdsSet.has(contact.id)"
              :aria-label="
                $t('CONTACTS_LAYOUT.TABLE.SELECT_CONTACT', {
                  name: contact.name,
                })
              "
              @change="event => toggleContact(contact.id, event.target.checked)"
            />
          </td>
          <td class="px-3 py-2">
            <button
              type="button"
              class="flex w-full min-w-0 items-center gap-3 text-start"
              @click="onClickViewDetails(contact.id)"
            >
              <Avatar
                :name="contact.name"
                :src="contact.thumbnail"
                :size="36"
                :status="contact.availabilityStatus"
                hide-offline-status
              />
              <span class="min-w-0">
                <span
                  class="block truncate text-sm font-medium text-n-slate-12 group-hover:text-n-blue-11"
                >
                  {{ contact.name }}
                </span>
                <span class="block truncate text-xs text-n-slate-10">
                  {{ contact.identifier || '#' + contact.id }}
                </span>
              </span>
              <span
                v-if="contact.blocked"
                class="rounded bg-n-ruby-3 px-1.5 py-0.5 text-xs text-n-ruby-11"
              >
                {{ $t('CONTACTS_LAYOUT.TABLE.BLOCKED') }}
              </span>
            </button>
          </td>
          <td class="px-3 py-2">
            <div class="flex min-w-0 flex-col gap-0.5">
              <span
                class="truncate text-sm text-n-slate-11"
                :title="contact.email"
              >
                {{ contact.email || $t('CONTACTS_LAYOUT.TABLE.NO_EMAIL') }}
              </span>
              <span class="truncate text-xs text-n-slate-10">
                {{
                  contact.phoneNumber ||
                  $t('CONTACTS_LAYOUT.TABLE.NO_PHONE_NUMBER')
                }}
              </span>
            </div>
          </td>
          <td class="px-3 py-2">
            <div
              class="flex min-w-0 items-center gap-1.5 text-sm text-n-slate-11"
            >
              <span class="i-lucide-building-2 size-3.5 shrink-0" />
              <span class="truncate" :title="getCompany(contact)">
                {{ getCompany(contact) }}
              </span>
            </div>
          </td>
          <td class="px-3 py-2">
            <div
              v-if="getChannels(contact).length"
              class="flex min-w-0 items-center gap-1"
            >
              <span
                class="max-w-28 truncate rounded bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                :title="getChannels(contact)[0]"
              >
                {{ getChannels(contact)[0] }}
              </span>
              <span
                v-if="getChannels(contact).length > 1"
                class="shrink-0 text-xs text-n-slate-10"
              >
                {{
                  $t('CONTACTS_LAYOUT.TABLE.MORE_INBOXES', {
                    count: getChannels(contact).length - 1,
                  })
                }}
              </span>
            </div>
            <span v-else class="text-xs text-n-slate-9">
              {{ $t('CONTACTS_LAYOUT.TABLE.NO_CHANNELS') }}
            </span>
          </td>
          <td class="px-3 py-2">
            <div
              class="flex min-w-0 items-center gap-1.5 text-sm text-n-slate-11"
            >
              <Flag
                v-if="getCountryCode(contact)"
                :country="getCountryCode(contact)"
                class="size-3.5 shrink-0"
              />
              <span
                v-else
                class="i-lucide-map-pin size-3.5 shrink-0 text-n-slate-9"
              />
              <span class="truncate" :title="getLocation(contact)">
                {{ getLocation(contact) }}
              </span>
            </div>
          </td>
          <td class="px-3 py-2">
            <span class="text-sm text-n-slate-11">
              {{ formatLastActivity(contact.lastActivityAt) }}
            </span>
          </td>
          <td class="px-3 py-2 text-end">
            <Button
              icon="i-lucide-chevron-right"
              slate
              xs
              ghost
              :aria-label="
                $t('CONTACTS_LAYOUT.TABLE.VIEW_CONTACT', {
                  name: contact.name,
                })
              "
              @click="onClickViewDetails(contact.id)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
